import { AutoComplete, Modal } from 'antd';
import FormItem from 'antd/es/form/FormItem';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import IConditionSuggestion from '~Api/LoanDrawdowns/IConditionSuggestion';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import ILoanDrawdownCondition from '~Api/LoanDrawdowns/ILoanDrawdownCondition';
import {
    loanDrawdownConditionSuggestionsListAction,
    loanDrawdownConditionsAddAction,
} from '~LoanDrawdowns/actions';
import _ from 'lodash';
import { loanDrawdownConditionSuggestionsSelector } from '~LoanDrawdowns/selectors';
import { IGlobalState } from '~reducer';

interface IDrawdownConditionAddModalErrors {
    conditionName?: string;
}

interface IOption {
    label: string;
    score: number;
    value: string;
}

export default function ConditionAddModal(props: {
    loanDrawdown: ILoanDrawdown;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const {
        loanDrawdown,
        isOpen,
        onCancel,
    } = props;

    const [ conditionName, setConditionName ] = useState<string>(null);
    const [ errors, setErrors ] = useState<IDrawdownConditionAddModalErrors>({});
    const [ options, setOptions ] = useState<IOption[]>([]);

    const suggestions: IConditionSuggestion[] = useSelector((state: IGlobalState): IConditionSuggestion[] => loanDrawdownConditionSuggestionsSelector(state, loanDrawdown.uuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!suggestions) {
            dispatch(loanDrawdownConditionSuggestionsListAction(loanDrawdown.uuid));
        }
    }, [
        dispatch,
        loanDrawdown.uuid,
        suggestions,
    ]);

    const onSelectConditionName: (value: string) => void = useCallback((value: string): void => {
        setConditionName(value);
    }, []);

    const validateConditionName: () => boolean = useCallback((): boolean => {
        let error: string;

        if (!conditionName || conditionName.length === 0) {
            error = 'Please enter the name';
        }

        setErrors((prevErrors: IDrawdownConditionAddModalErrors): IDrawdownConditionAddModalErrors => ({
            ...prevErrors,
            conditionName: error,
        }));

        return !error;
    }, [
        conditionName,
    ]);

    const onClickAdd: () => void = useCallback((): void => {
        const valid: boolean = validateConditionName();

        if (!valid) {
            return;
        }

        const loanDrawdownCondition: ILoanDrawdownCondition = {
            loanDrawdownUuid: loanDrawdown.uuid,
            name: conditionName,
        };
        dispatch(loanDrawdownConditionsAddAction(loanDrawdown.uuid, loanDrawdownCondition));

        setConditionName(null);
        onCancel();
    }, [
        conditionName,
        dispatch,
        loanDrawdown.uuid,
        onCancel,
        validateConditionName,
    ]);

    const onSearchConditionName: (value: string) => void = useCallback((value: string): void => {
        const searchValue: string = value.toLocaleLowerCase();

        const results: IOption[] = [];

        _.each(suggestions, (suggestion: IConditionSuggestion): void => {
            if (suggestion.name.toLocaleLowerCase().includes(searchValue)) {
                results.push({
                    label: suggestion.name,
                    score: suggestion.score,
                    value: suggestion.name,
                });
            }
        });

        setConditionName(value);
        setOptions(_.sortBy(results, [ 'score' ]));
    }, [
        suggestions,
    ]);

    return (
        <Modal
            destroyOnClose={true}
            okText='Add Condition'
            onCancel={onCancel}
            onOk={onClickAdd}
            open={isOpen}
            title={`Add Condition for Drawdown ${loanDrawdown.drawdownNumber}`}
            width={600}
            wrapClassName='loan-drawdown-condition-add-modal'
        >
            <FormItem
                help={errors.conditionName}
                label='Name'
                validateStatus={errors.conditionName && 'error'}
            >
                <AutoComplete
                    dropdownMatchSelectWidth={300}
                    maxLength={255}
                    onBlur={validateConditionName}
                    onSearch={onSearchConditionName}
                    onSelect={onSelectConditionName}
                    options={_.slice(options, 0, 20)}
                    value={conditionName}
                />
            </FormItem>
        </Modal>
    );
}

